import { useState, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ToasterProvider } from '@/components/ui/toaster';
import { AuthProvider } from '@/lib/auth';
import { createQueryClient } from '@/lib/queryClient';

/**
 * The provider stack.
 *
 * Order matters: `AuthProvider` sits INSIDE `QueryClientProvider` because it
 * registers the token provider the API client uses, and outside `ToasterProvider`
 * would mean a session failure could not raise a toast.
 *
 * The QueryClient is created in state, not at module scope. A module-level
 * client is shared between tests in the same file, so one test's cached data
 * leaks into the next and the failures are maddening to track down.
 */
export function Providers({ children }: { children: ReactNode }): JSX.Element {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <ToasterProvider>
        <TooltipProvider delayDuration={300}>
          <AuthProvider>{children}</AuthProvider>
        </TooltipProvider>
      </ToasterProvider>
    </QueryClientProvider>
  );
}
